import { ACCEPTANCE_METHODS, type CaseDto, type JoinableOfferDto, type MeDto, type OfferDto, type OfferListItemDto, type OfferVersionDto, type ShareDto, datePl } from '@bqcrm/shared';
import { Anchor, Badge, Button, Card, Checkbox, Group, Modal, Radio, Stack, Table, Text, Title } from '@mantine/core';
import { IconCircleCheckFilled, IconPlus, IconSend } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { CompanyBadgeById, TradeBadge, VersionStatusBadge } from '../../components/Badges';
import { Money } from '../../components/Money';
import { QueryState } from '../../components/QueryState';
import { useCompanies, useTrades } from '../../data';
import { formatDateTime } from '../../format';
import { EmptyState } from '../../components/ui';
import { notifyError, notifyOk } from '../../notify';
import { ShareDialog } from '../offer/offer-ui';
import { ask } from '../../confirm';

interface OutboxStatus {
  status: string;
  sentAt: string | null;
  lastError: string | null;
}

const MAIL_STATUS: Record<string, { label: string; color: string }> = {
  oczekuje: { label: 'czeka na wysyłkę', color: 'gray' },
  wysylanie: { label: 'wysyłanie', color: 'blue' },
  wyslany: { label: 'wysłany', color: 'green' },
  blad: { label: 'błąd — ponowienie', color: 'orange' },
  blad_trwaly: { label: 'błąd wysyłki', color: 'red' },
  do_sprawdzenia: { label: 'do sprawdzenia', color: 'red' },
};

function MailState({ shareId }: { shareId: string }) {
  const q = useQuery({ queryKey: ['outbox', 'udostepnienie', shareId], queryFn: () => api<OutboxStatus[]>(`/outbox/udostepnienie/${shareId}`), refetchInterval: 10_000 });
  const link = q.data?.[q.data.length - 1];
  if (!link) return <Text size="xs" c="dimmed">bez e-maila</Text>;
  const s = MAIL_STATUS[link.status] ?? { label: link.status, color: 'gray' };
  return (
    <Badge size="xs" variant="light" color={s.color} title={link.lastError ?? undefined}>
      e-mail: {s.label}
    </Badge>
  );
}

/** preselect — zakres z podpowiedzi „Przygotuj ofertę”: jego firma i jej zakresy jeszcze bez oferty (etap „otwarty”). */
function NewOfferDialog({ c, preselect, onClose }: { c: CaseDto; preselect?: string | null; onClose: () => void }) {
  const trades = useTrades();
  const companies = useCompanies();
  const navigate = useNavigate();
  // zakresy przypisane innym handlowcom (zadanie ofertowe) — bez możliwości dodania do mojej oferty
  const blocked = c.can.offerBlocked ?? {};
  const active = c.scopes.filter((s) => s.salesStage !== 'przegrany');
  const allowed = (s: { id: string }) => !(s.id in blocked);
  const companyIds = [...new Set(active.map((s) => s.companyId))];
  const pre = active.find((s) => s.id === preselect);
  const firstCompany = pre?.companyId ?? companyIds[0] ?? '';
  const [companyId, setCompanyId] = useState(firstCompany);
  const [selected, setSelected] = useState<string[]>(() =>
    (pre ? active.filter((s) => s.companyId === firstCompany && (s.id === pre.id || s.salesStage === 'otwarty')) : active.filter((s) => s.companyId === firstCompany)).filter(allowed).map((s) => s.id),
  );
  const create = useMutation({
    mutationFn: () => api<OfferDto>('/offers', { method: 'POST', body: { caseId: c.id, scopeIds: selected } }),
    onSuccess: (o) => navigate(`/oferty/${o.id}`),
    onError: notifyError,
  });
  // oferta wspólna: niewydane oferty tej firmy w sprawie — można dopisać do nich swoją część zamiast nowej oferty
  const joinableQ = useQuery({ queryKey: ['offers-joinable', c.id], queryFn: () => api<JoinableOfferDto[]>(`/cases/${c.id}/offers/joinable`) });
  const joinable = (joinableQ.data ?? []).filter((o) => o.companyId === companyId && !o.mine);
  const [target, setTarget] = useState<string>('nowa');
  useEffect(() => {
    // domyślnie dopisanie do istniejącej (jedna oferta dla klienta) — osobna oferta nadal do wyboru
    setTarget(joinable[0]?.offerId ?? 'nowa');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, joinableQ.data]);
  const joinTarget = joinable.find((o) => o.offerId === target) ?? null;
  const inTarget = (id: string) => !!joinTarget?.parts.some((p) => p.scopeId === id);
  const join = useMutation({
    mutationFn: () => api<OfferVersionDto>(`/offers/${target}/join`, { method: 'POST', body: { scopeIds: selected.filter((id) => !inTarget(id)) } }),
    onSuccess: (v) => {
      notifyOk('Dopisano Twoją część do oferty — uzupełnij pozycje swojej branży');
      navigate(`/oferty/${v.offerId}`);
    },
    onError: notifyError,
  });
  return (
    <Stack>
      <Text size="sm">Oferta obejmuje zakresy jednej firmy (numer i dane wystawcy firmy). Dla zakresów drugiej firmy przygotuj osobną ofertę — klient może dostać obie w jednym linku.</Text>
      <Radio.Group
        label="Firma wystawiająca"
        value={companyId}
        onChange={(v) => {
          setCompanyId(v);
          setSelected(active.filter((s) => s.companyId === v).filter(allowed).map((s) => s.id));
        }}
      >
        <Stack gap={4} mt={4}>
          {companyIds.map((id) => (
            <Radio key={id} value={id} label={companies.data?.find((x) => x.id === id)?.shortName ?? id} />
          ))}
        </Stack>
      </Radio.Group>
      {joinable.length > 0 && (
        <Radio.Group label="Gdzie przygotować Twoją część" value={target} onChange={setTarget}>
          <Stack gap={6} mt={4}>
            {joinable.map((o) => (
              <Radio
                key={o.offerId}
                value={o.offerId}
                label={`Dopisz do oferty w przygotowaniu: ${o.title}`}
                description={`Autor: ${o.authorName ?? '—'} · branże: ${o.parts.map((p) => `${p.tradeName} (${p.preparedByName})`).join(', ')} — klient dostanie jedną ofertę`}
              />
            ))}
            <Radio value="nowa" label="Utwórz osobną ofertę" description="Twoja część jako oddzielny dokument dla klienta" />
          </Stack>
        </Radio.Group>
      )}
      <Stack gap={4}>
        <Text size="sm" fw={500}>
          {joinTarget ? 'Twoje branże do dopisania' : 'Zakresy w ofercie'}
        </Text>
        {active
          .filter((s) => s.companyId === companyId)
          .map((s) => (
            <Checkbox
              key={s.id}
              disabled={!allowed(s) || inTarget(s.id)}
              description={inTarget(s.id) ? `już w tej ofercie — przygotowuje ${joinTarget?.parts.find((p) => p.scopeId === s.id)?.preparedByName ?? ''}` : s.id in blocked ? (blocked[s.id] ? `ofertę przygotowuje ${blocked[s.id]} (przypisane zadanie)` : 'nie obsługujesz tej branży (brak funkcji handlowca w branży ani przypisanego zadania)') : undefined}
              checked={selected.includes(s.id) && !inTarget(s.id)}
              onChange={(e) => {
            const on = e.currentTarget.checked;
            setSelected((x) => (on ? [...x, s.id] : x.filter((y) => y !== s.id)));
          }}
              label={trades.data?.find((t) => t.code === s.tradeCode)?.name ?? s.tradeCode}
            />
          ))}
      </Stack>
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          Anuluj
        </Button>
        {joinTarget ? (
          <Button onClick={() => join.mutate()} loading={join.isPending} disabled={!selected.some((id) => !inTarget(id))}>
            Dopisz moją część
          </Button>
        ) : (
          <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!selected.length}>
            Utwórz ofertę
          </Button>
        )}
      </Group>
    </Stack>
  );
}

/** newFor — zakres z linku (?nowa-oferta=…): otwiera okno nowej oferty; oferta powstaje dopiero po „Utwórz ofertę”. */
export function OffersTab({ c, me, newFor, onNewHandled }: { c: CaseDto; me: MeDto; newFor?: string | null; onNewHandled?: () => void }) {
  const offers = useQuery({ queryKey: ['case-offers', c.id], queryFn: () => api<OfferListItemDto[]>(`/cases/${c.id}/offers`) });
  const shares = useQuery({ queryKey: ['case-shares', c.id], queryFn: () => api<ShareDto[]>(`/cases/${c.id}/shares`) });
  const [newOpen, setNewOpen] = useState(false);
  const [preselect, setPreselect] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  /** wersje zaznaczone w oknie udostępnienia z podpowiedzi przy starym linku (null — wszystkie wydane) */
  const [sharePre, setSharePre] = useState<string[] | null>(null);
  const qc = useQueryClient();
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/shares/${id}/revoke`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['case-shares', c.id] });
      notifyOk('Link unieważniony');
    },
    onError: notifyError,
  });
  const companyIds = [...new Set((offers.data ?? []).map((o) => o.companyId))];
  const canCreate = c.can.createOffer;
  useEffect(() => {
    if (!newFor) return;
    if (canCreate) {
      setPreselect(newFor);
      setNewOpen(true);
    }
    onNewHandled?.();
  }, [newFor, canCreate, onNewHandled]);
  const closeNew = () => {
    setNewOpen(false);
    setPreselect(null);
  };

  return (
    <QueryState isLoading={offers.isLoading} error={offers.error}>
      <Group justify="space-between" mb="sm" wrap="wrap">
        <Text size="sm" c="dimmed" style={{ flex: '1 1 260px' }}>
          Sprawa może mieć wiele ofert; każda dotyczy zakresów jednej firmy.
        </Text>
        <Group gap="xs" wrap="wrap">
          {can(me, 'oferty.udostepnianie') && (offers.data ?? []).some((o) => ['wydany', 'zaakceptowany'].includes(o.latest.effectiveStatus)) && (
            <Button size="xs" variant="light" leftSection={<IconSend size={14} />} onClick={() => setShareOpen(true)}>
              Udostępnij klientowi
            </Button>
          )}
          {canCreate && (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => (setPreselect(null), setNewOpen(true))}>
              Nowa oferta
            </Button>
          )}
        </Group>
      </Group>
      {!offers.data?.length && <EmptyState compact title="Brak ofert" hint={canCreate ? 'Przygotuj ofertę dla zakresów jednej firmy.' : undefined} />}
      <Stack>
        {companyIds.map((cid) => (
          <Card key={cid} withBorder padding="sm">
            <Group mb="xs">
              <CompanyBadgeById id={cid} full />
            </Group>
            <Table.ScrollContainer minWidth={560}>
            <Table verticalSpacing={6} highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Oferta</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Warianty (brutto)</Table.Th>
                  <Table.Th>Akceptacja</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(offers.data ?? [])
                  .filter((o) => o.companyId === cid)
                  .map((o) => (
                    <Table.Tr key={o.id}>
                      <Table.Td>
                        <Anchor component={Link} to={`/oferty/${o.id}`} fw={600} size="sm">
                          {o.latest.issuedNumber ?? o.number ?? 'Oferta robocza'}
                        </Anchor>
                        <Group gap={4} mt={2}>
                          {o.scopeIds.map((sid) => {
                            const s = c.scopes.find((x) => x.id === sid);
                            return s ? <TradeBadge key={sid} code={s.tradeCode} /> : null;
                          })}
                          <Text size="xs" c="dimmed">
                            wersji: {o.versionsCount}
                          </Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <VersionStatusBadge status={o.latest.effectiveStatus} caseClosed={!!c.closedAt} />
                        <Text size="xs" c="dimmed">
                          ważna do {datePl(o.latest.validUntil)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {o.variantTotals.map((v) => (
                          <Text key={v.name} size="xs">
                            {v.name}: <Money gr={v.grossGr} />
                          </Text>
                        ))}
                      </Table.Td>
                      <Table.Td>
                        {o.acceptance ? (
                          <Group gap={4} wrap="nowrap" align="flex-start">
                          <IconCircleCheckFilled size={14} color="var(--mantine-color-green-6)" style={{ flexShrink: 0, marginTop: 1 }} />
                          <Text size="xs" c="green">
                            {o.acceptance.variantName} (v{o.acceptance.versionNo}) · {ACCEPTANCE_METHODS[o.acceptance.method]} · {formatDateTime(o.acceptance.acceptedAt)}
                          </Text>
                          </Group>
                        ) : (
                          <Text size="xs" c="dimmed">
                            bez akceptacji
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
              </Table.Tbody>
            </Table>
            </Table.ScrollContainer>
          </Card>
        ))}
      </Stack>

      {!!shares.data?.length && (
        <>
          <Title order={6} mt="lg" mb="xs">
            Linki dla klienta
          </Title>
          <Table.ScrollContainer minWidth={560}>
          <Table verticalSpacing={4}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Oferty i odbiorca</Table.Th>
                <Table.Th>E-mail</Table.Th>
                <Table.Th>Otwarcia i ważność</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {shares.data.map((s) => (
                <Table.Tr key={s.id} style={{ opacity: s.revokedAt ? 0.5 : 1 }}>
                  <Table.Td>
                    <Group gap={6} wrap="wrap">
                      {s.versions.map((v) => (
                        <Group key={v.id} gap={4} wrap="nowrap">
                          <Text size="sm">{v.issuedNumber}</Text>
                          {(v.effectiveStatus === 'zastapiony' || v.effectiveStatus === 'wygasly') && <VersionStatusBadge status={v.effectiveStatus} size="xs" />}
                        </Group>
                      ))}
                    </Group>
                    <Text size="xs" c="dimmed">
                      {s.recipientEmail} · utworzył(a) {s.createdByName} {formatDateTime(s.createdAt)}
                    </Text>
                    {(() => {
                      // QA P12: link prowadzi do zastąpionej wersji — podpowiedź udostępnienia aktualnej
                      if (s.revokedAt || !can(me, 'oferty.udostepnianie')) return null;
                      const newer = s.versions
                        .filter((v) => v.effectiveStatus === 'zastapiony')
                        .map((v) => (offers.data ?? []).find((o) => o.id === v.offerId)?.latest)
                        .filter((l): l is NonNullable<typeof l> => !!l && (l.effectiveStatus === 'wydany' || l.effectiveStatus === 'zaakceptowany') && !s.versions.some((v) => v.id === l.id));
                      if (!newer.length) return null;
                      return (
                        <Group gap={6} mt={2} wrap="wrap">
                          <Text size="xs" c="orange">
                            Klient widzi tu starszą wersję. Wyślij aktualną, a stary link możesz unieważnić.
                          </Text>
                          <Button size="compact-xs" variant="light" onClick={() => (setSharePre(newer.map((l) => l.id)), setShareOpen(true))}>
                            Udostępnij {newer.map((l) => `v${l.versionNo}`).join(', ')}
                          </Button>
                        </Group>
                      );
                    })()}
                  </Table.Td>
                  <Table.Td>
                    <MailState shareId={s.id} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">
                      otwarć: {s.viewCount}
                      {s.lastViewedAt ? ` (ost. ${formatDateTime(s.lastViewedAt)})` : ''}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {s.revokedAt ? `unieważniony ${formatDateTime(s.revokedAt)}` : `ważny do ${datePl(s.expiresAt)}`}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {!s.revokedAt && can(me, 'oferty.udostepnianie') && (
                      <Button size="compact-xs" variant="subtle" color="red" onClick={() => void ask('Klient straci dostęp do ofert pod tym linkiem.', { title: 'Unieważnić link?', confirm: 'Unieważnij', danger: true }).then((ok) => ok && revoke.mutate(s.id))}>
                        Unieważnij
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        </>
      )}

      <Modal opened={newOpen} onClose={closeNew} title="Nowa oferta">
        {newOpen && <NewOfferDialog c={c} preselect={preselect} onClose={closeNew} />}
      </Modal>
      <ShareDialog
        opened={shareOpen}
        onClose={() => (setShareOpen(false), setSharePre(null))}
        c={c}
        preselect={sharePre ?? (offers.data ?? []).filter((o) => o.latest.effectiveStatus === 'wydany').map((o) => o.latest.id)}
        defaultEmail={c.client.emails[0] ?? null}
      />
    </QueryState>
  );
}
